// A low-poly two-wheeled handcart — mobile storage you pull yourself. An open
// wooden bed on a single axle with two side wheels, and two handles raked up
// toward the front (+z, the direction it is pulled). Ported 1:1 from age-of-mcp's
// classic handcart.ts, restoring its wheel + shaft loops.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const WOOD  = "#a07040";   // warm planking (matches the boat hull)
const TRIM  = "#6b4820";   // darker frame & rails
const WHEEL = "#33271a";   // dark wheels
const IRON  = "#5a5550";   // axle

const W    = 0.30;   // bed width  (x)
const L    = 0.34;   // bed length (z)
const BEDY = 0.16;   // bed floor height, above the wheels
const WR   = 0.10;   // wheel radius

function partsFor(): Part[] {
  const parts: Part[] = [];

  // Bed floor.
  parts.push({ geometry: new THREE.BoxGeometry(W, 0.04, L), color: WOOD, position: [0, BEDY, 0] });

  // Side & end rails (front +z left open for loading).
  parts.push({ geometry: new THREE.BoxGeometry(0.03, 0.11, L), color: TRIM, position: [ W / 2 - 0.015, BEDY + 0.06, 0] });
  parts.push({ geometry: new THREE.BoxGeometry(0.03, 0.11, L), color: TRIM, position: [-W / 2 + 0.015, BEDY + 0.06, 0] });
  parts.push({ geometry: new THREE.BoxGeometry(W, 0.11, 0.03), color: TRIM, position: [0, BEDY + 0.06, -L / 2 + 0.015] });
  parts.push({ geometry: new THREE.BoxGeometry(W, 0.06, 0.03), color: TRIM, position: [0, BEDY + 0.04,  L / 2 - 0.015] });

  // Axle.
  parts.push({
    geometry: new THREE.CylinderGeometry(0.018, 0.018, W + 0.10, 6),
    color: IRON, position: [0, WR, -0.02], rotation: [0, 0, Math.PI / 2],
  });

  // Wheels (discs on the axle, rolling along z).
  for (const sx of [-1, 1]) {
    parts.push({
      geometry: new THREE.CylinderGeometry(WR, WR, 0.05, 10),
      color: WHEEL,
      position: [sx * (W / 2 + 0.03), WR, -0.02],
      rotation: [0, 0, Math.PI / 2],
    });
  }

  // Pull shafts (two rails running from inside the bed up & forward, +z),
  // anchored back into the bed body so they read as JOINED, not floating.
  for (const sx of [-1, 1]) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.028, 0.028, 0.5),
      color: WOOD,
      position: [sx * (W / 2 - 0.04), BEDY + 0.10, L / 2 + 0.02],
      rotation: [0.5, 0, 0],
    });
  }
  // Cross grip joining the two shafts at the pulling end.
  parts.push({
    geometry: new THREE.BoxGeometry(W - 0.04, 0.026, 0.026),
    color: TRIM,
    position: [0, BEDY + 0.22, L / 2 + 0.24],
  });

  return parts;
}

export const handcart = defineModel({
  id: "handcart",
  batchId: "standard",
  name: "Handcart",
  tags: ["vehicle","handcart","low-poly","age-of-mcp","classic"],
  summary: "Low-poly handcart.",
  description: "Ported from the age-of-mcp gallery.",
  author: "age-of-mcp",
  footprint: 0.4,
  parts: () => partsFor(),
  animations: [],
});
